import { cn } from "@/lib/utils";

interface QuickReply {
  label: string;
  message: string;
}

interface QuickRepliesProps {
  replies: QuickReply[];
  onSelect: (message: string, label: string) => void;
  isLoading?: boolean;
}

const QuickReplies = ({ replies, onSelect, isLoading }: QuickRepliesProps) => {
  if (isLoading || replies.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 animate-fade-in">
      {replies.map((reply) => (
        <button
          key={reply.label}
          onClick={() => onSelect(reply.message, reply.label)}
          className={cn(
            "text-xs px-3 py-1.5 rounded-full border border-primary/30",
            "text-primary bg-primary/5 hover:bg-primary/10",
            "transition-all duration-200 hover:scale-[1.02]",
            "focus:outline-none focus:ring-2 focus:ring-primary/30"
          )}
        >
          {reply.label}
        </button>
      ))}
    </div>
  );
};

export default QuickReplies;

// Helper function to determine which quick replies to show based on conversation context
export const getContextualReplies = (
  messages: { role: "user" | "assistant"; content: string }[]
): QuickReply[] => {
  const lastAssistantMessage = [...messages]
    .reverse()
    .find((m) => m.role === "assistant");

  if (!lastAssistantMessage) {
    // Initial state - show general options
    return [
      { label: "Get a Free Quote", message: "I'd like to get a free quote for my property" },
      { label: "View Services", message: "What pressure washing services do you offer?" },
      { label: "Service Areas", message: "What areas do you cover?" },
    ];
  }

  const content = lastAssistantMessage.content.toLowerCase();

  // After discussing services
  if (content.includes("service") || content.includes("offer") || content.includes("clean")) {
    return [
      { label: "Get a Quote", message: "I'd like to get a quote for this service" },
      { label: "See Pricing", message: "What are your prices?" },
      { label: "Book Now", message: "How do I book a service?" },
    ];
  }

  // After discussing areas
  if (content.includes("area") || content.includes("cover") || content.includes("location")) {
    return [
      { label: "Check My Postcode", message: "Can you check if you cover my area?" },
      { label: "Get a Quote", message: "I'd like to request a quote" },
    ];
  }

  // After discussing pricing
  if (content.includes("price") || content.includes("cost") || content.includes("£") || content.includes("quote")) {
    return [
      { label: "Request Quote", message: "I'd like to request a free quote" },
      { label: "Contact You", message: "How can I get in touch with you?" },
      { label: "What's Included?", message: "What's included in the service?" },
    ];
  }

  // After discussing booking
  if (content.includes("book") || content.includes("contact") || content.includes("schedule")) {
    return [
      { label: "Go to Contact Page", message: "Can you give me a link to the contact page?" },
      { label: "Phone Number", message: "What's your phone number?" },
    ];
  }

  // Default follow-up options
  return [
    { label: "Get a Quote", message: "I'd like to get a free quote" },
    { label: "Ask Another Question", message: "I have another question" },
  ];
};
